import type { Resource, ScienceSymbol } from '@sw/engine';
import type { CSSProperties, ReactNode } from 'react';

/** Gradientes y filtros compartidos por todos los iconos (se monta una vez en la raíz). */
export function SvgDefs() {
  return (
    <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true" focusable="false">
      <SvgDefsContent />
    </svg>
  );
}

/** Contenido de <defs> (también se incrusta en las caras de carta rasterizadas para la mesa 3D). */
export function SvgDefsContent() {
  return (
    <defs>
      <linearGradient id="g-gold" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#fff1b8" />
        <stop offset="0.45" stopColor="#e9bf55" />
        <stop offset="1" stopColor="#9a6a17" />
      </linearGradient>
      <radialGradient id="g-coin" cx="0.38" cy="0.32" r="0.75">
        <stop offset="0" stopColor="#fff5c8" />
        <stop offset="0.5" stopColor="#e7b844" />
        <stop offset="1" stopColor="#8f5f12" />
      </radialGradient>
      <linearGradient id="g-wood" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#c58a4e" />
        <stop offset="1" stopColor="#6f4220" />
      </linearGradient>
      <linearGradient id="g-stone" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#e1e3e6" />
        <stop offset="1" stopColor="#7f848c" />
      </linearGradient>
      <linearGradient id="g-clay" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#ee8b5a" />
        <stop offset="1" stopColor="#9c3f1d" />
      </linearGradient>
      <linearGradient id="g-ore" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#7d8796" />
        <stop offset="1" stopColor="#262b33" />
      </linearGradient>
      <linearGradient id="g-glass" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#c9f4ff" />
        <stop offset="1" stopColor="#2e9fc4" />
      </linearGradient>
      <linearGradient id="g-papyrus" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#fbeec4" />
        <stop offset="1" stopColor="#c7a560" />
      </linearGradient>
      <linearGradient id="g-textile" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#c48ae3" />
        <stop offset="1" stopColor="#5f2a83" />
      </linearGradient>
      <linearGradient id="g-shield" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#f07a62" />
        <stop offset="1" stopColor="#8e2318" />
      </linearGradient>
      <linearGradient id="g-laurel" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#bfe39a" />
        <stop offset="1" stopColor="#4b7f2c" />
      </linearGradient>
      <linearGradient id="g-science" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#9ee2a8" />
        <stop offset="1" stopColor="#2c7a3c" />
      </linearGradient>
      <filter id="f-drop" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="1" stdDeviation="0.8" floodColor="#000" floodOpacity="0.55" />
      </filter>
      <filter id="f-paper">
        <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="3" />
        <feColorMatrix values="0 0 0 0 0.35  0 0 0 0 0.25  0 0 0 0 0.12  0 0 0 0.10 0" />
        <feComposite in2="SourceGraphic" operator="in" />
      </filter>
    </defs>
  );
}

interface IconProps {
  size?: number;
  title?: string;
  style?: CSSProperties;
  className?: string;
}

function Svg({ size = 22, title, children, style, className }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      style={{ flex: 'none', ...style }}
      className={className}
    >
      {title ? <title>{title}</title> : null}
      <g filter="url(#f-drop)">{children}</g>
    </svg>
  );
}

const RESOURCE_SHAPES: Record<Resource, ReactNode> = {
  wood: (
    <>
      <circle cx="16" cy="16" r="14" fill="#2a1a0c" />
      <rect x="6" y="9" width="20" height="6" rx="3" fill="url(#g-wood)" />
      <rect x="6" y="17" width="20" height="6" rx="3" fill="url(#g-wood)" />
      <circle cx="24" cy="12" r="2.4" fill="#e8c08c" stroke="#7a4a22" strokeWidth="0.8" />
      <circle cx="24" cy="20" r="2.4" fill="#e8c08c" stroke="#7a4a22" strokeWidth="0.8" />
    </>
  ),
  stone: (
    <>
      <circle cx="16" cy="16" r="14" fill="#2b2e33" />
      <rect x="6" y="15" width="10" height="8" rx="1.5" fill="url(#g-stone)" />
      <rect x="16.5" y="15" width="10" height="8" rx="1.5" fill="url(#g-stone)" />
      <rect x="10.5" y="7" width="11" height="8" rx="1.5" fill="url(#g-stone)" />
    </>
  ),
  clay: (
    <>
      <circle cx="16" cy="16" r="14" fill="#3a1a0e" />
      <path d="M7 19 l9 -8 l9 8 l-9 5z" fill="url(#g-clay)" />
      <path d="M7 19 l9 5 v3 l-9 -5z" fill="#7a2f13" />
      <path d="M25 19 l-9 5 v3 l9 -5z" fill="#a8461f" />
    </>
  ),
  ore: (
    <>
      <circle cx="16" cy="16" r="14" fill="#15181d" />
      <path d="M7 21 l4 -9 l6 -3 l7 4 l2 8 l-7 4 l-9 -1z" fill="url(#g-ore)" />
      <path d="M13 13 l4 -2 l3 3 l-4 2z" fill="#c7d2e0" opacity="0.8" />
      <path d="M18 19 l3 -1 l1 2 l-3 1z" fill="#c7d2e0" opacity="0.6" />
    </>
  ),
  glass: (
    <>
      <circle cx="16" cy="16" r="14" fill="#0d2a36" />
      <path d="M13 6 h6 v6 l5 9 a4 4 0 0 1 -3.5 6 h-9 a4 4 0 0 1 -3.5 -6 l5 -9z" fill="url(#g-glass)" />
      <rect x="12" y="5" width="8" height="2.4" rx="1" fill="#e8fbff" />
      <path d="M12 20 q4 2 9 0" stroke="#e8fbff" strokeWidth="1.2" fill="none" opacity="0.7" />
    </>
  ),
  papyrus: (
    <>
      <circle cx="16" cy="16" r="14" fill="#3a2c12" />
      <rect x="8" y="8" width="16" height="16" rx="1" fill="url(#g-papyrus)" />
      <rect x="6" y="6.5" width="20" height="3.5" rx="1.75" fill="#b48a3e" />
      <rect x="6" y="22" width="20" height="3.5" rx="1.75" fill="#b48a3e" />
      <path d="M11 13 h10 M11 16 h10 M11 19 h7" stroke="#8a6a2b" strokeWidth="1.1" />
    </>
  ),
  textile: (
    <>
      <circle cx="16" cy="16" r="14" fill="#2a1238" />
      <path d="M7 10 q9 -4 18 0 v12 q-9 4 -18 0z" fill="url(#g-textile)" />
      <path
        d="M7 14 q9 -4 18 0 M7 18 q9 -4 18 0"
        stroke="#e2c1f2"
        strokeWidth="1"
        fill="none"
        opacity="0.7"
      />
    </>
  ),
};

export function ResourceIcon({ r, ...p }: IconProps & { r: Resource }) {
  return <Svg {...p}>{RESOURCE_SHAPES[r]}</Svg>;
}

export function CoinIcon({ value, ...p }: IconProps & { value?: number | string }) {
  return (
    <Svg {...p}>
      <circle cx="16" cy="16" r="13" fill="url(#g-coin)" stroke="#7a4f0e" strokeWidth="1.2" />
      <circle cx="16" cy="16" r="9.5" fill="none" stroke="#a97b23" strokeWidth="1" opacity="0.8" />
      {value !== undefined ? (
        <text
          x="16"
          y="21"
          textAnchor="middle"
          fontSize="13"
          fontWeight="800"
          fill="#4a2f05"
          fontFamily="Cinzel, serif"
        >
          {value}
        </text>
      ) : null}
    </Svg>
  );
}

export function ShieldIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path
        d="M16 3 l11 4 v8 c0 7 -5 11.5 -11 14 c-6 -2.5 -11 -7 -11 -14 v-8z"
        fill="url(#g-shield)"
        stroke="#5a140c"
        strokeWidth="1.2"
      />
      <path d="M11 11 l10 10 M21 11 l-10 10" stroke="#ffe0d6" strokeWidth="2.2" strokeLinecap="round" />
    </Svg>
  );
}

export function LaurelIcon({ value, ...p }: IconProps & { value?: number | string }) {
  return (
    <Svg {...p}>
      <circle cx="16" cy="16" r="13.5" fill="#f4f0dc" stroke="url(#g-laurel)" strokeWidth="2.4" />
      <path d="M8 22 q-3 -7 1 -13 M24 22 q3 -7 -1 -13" stroke="#4b7f2c" strokeWidth="1.6" fill="none" />
      {value !== undefined ? (
        <text
          x="16"
          y="21"
          textAnchor="middle"
          fontSize="13"
          fontWeight="800"
          fill="#2b4d17"
          fontFamily="Cinzel, serif"
        >
          {value}
        </text>
      ) : null}
    </Svg>
  );
}

const SCIENCE_SHAPES: Record<ScienceSymbol, ReactNode> = {
  compass: (
    <>
      <circle cx="16" cy="16" r="14" fill="url(#g-science)" />
      <circle cx="16" cy="8" r="2.2" fill="#f2f7ee" />
      <path d="M16 9 l-6 15 M16 9 l6 15" stroke="#f2f7ee" strokeWidth="2.4" strokeLinecap="round" />
      <path d="M11 19 q5 3 10 0" stroke="#f2f7ee" strokeWidth="1.6" fill="none" />
    </>
  ),
  gear: (
    <>
      <circle cx="16" cy="16" r="14" fill="url(#g-science)" />
      <g fill="#f2f7ee">
        {Array.from({ length: 8 }, (_, i) => (
          <rect
            key={i}
            x="14.4"
            y="5"
            width="3.2"
            height="5"
            rx="0.8"
            transform={`rotate(${i * 45} 16 16)`}
          />
        ))}
        <circle cx="16" cy="16" r="7" />
      </g>
      <circle cx="16" cy="16" r="3" fill="#2c7a3c" />
    </>
  ),
  tablet: (
    <>
      <circle cx="16" cy="16" r="14" fill="url(#g-science)" />
      <path
        d="M10 8 h12 a2 2 0 0 1 2 2 v13 a2 2 0 0 1 -2 2 h-12 a2 2 0 0 1 -2 -2 v-13 a2 2 0 0 1 2 -2z"
        fill="#f2f7ee"
      />
      <path d="M11.5 12 h9 M11.5 15.5 h9 M11.5 19 h6" stroke="#2c7a3c" strokeWidth="1.4" />
    </>
  ),
};

export function ScienceIcon({ symbol, ...p }: IconProps & { symbol: ScienceSymbol }) {
  return <Svg {...p}>{SCIENCE_SHAPES[symbol]}</Svg>;
}

export function WildScienceIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="16" cy="16" r="14" fill="url(#g-science)" />
      <text
        x="16"
        y="22"
        textAnchor="middle"
        fontSize="16"
        fontWeight="800"
        fill="#f2f7ee"
        fontFamily="Cinzel, serif"
      >
        ?
      </text>
    </Svg>
  );
}

export function StageIcon({ built, ...p }: IconProps & { built?: boolean }) {
  return (
    <Svg {...p}>
      <path
        d="M4 26 h24 l-12 -20z"
        fill={built ? 'url(#g-gold)' : '#3b3a46'}
        stroke={built ? '#7a5512' : '#6f6b80'}
        strokeWidth="1.2"
      />
      <path
        d="M10 26 l6 -10 l6 10"
        fill="none"
        stroke={built ? '#7a5512' : '#6f6b80'}
        strokeWidth="1"
        opacity="0.7"
      />
    </Svg>
  );
}

export function ArrowIcon({ dir, ...p }: IconProps & { dir: 'left' | 'right' | 'both' }) {
  return (
    <Svg {...p}>
      {dir !== 'right' ? <path d="M13 9 l-8 7 l8 7z" fill="url(#g-gold)" /> : null}
      {dir !== 'left' ? <path d="M19 9 l8 7 l-8 7z" fill="url(#g-gold)" /> : null}
      <rect
        x={dir === 'right' ? 6 : 11}
        y="14"
        width={dir === 'both' ? 10 : 14}
        height="4"
        rx="2"
        fill="url(#g-gold)"
      />
    </Svg>
  );
}

export function CrownIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path
        d="M5 23 l2 -12 l6 6 l3 -9 l3 9 l6 -6 l2 12z"
        fill="url(#g-gold)"
        stroke="#7a5512"
        strokeWidth="1"
      />
      <rect x="5" y="23" width="22" height="3.5" rx="1" fill="#b78a2a" />
    </Svg>
  );
}

export function DiscardIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <rect
        x="7"
        y="6"
        width="16"
        height="22"
        rx="2"
        fill="#2a2d44"
        stroke="#8c86a8"
        strokeWidth="1.2"
        transform="rotate(-10 15 17)"
      />
      <rect x="10" y="5" width="16" height="22" rx="2" fill="#363a58" stroke="#b1abd0" strokeWidth="1.2" />
    </Svg>
  );
}

export function ChainIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="4" y="11" width="14" height="10" rx="5" fill="none" stroke="url(#g-gold)" strokeWidth="3" />
      <rect x="14" y="11" width="14" height="10" rx="5" fill="none" stroke="url(#g-gold)" strokeWidth="3" />
    </Svg>
  );
}

export function MagnifierIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="13.5" cy="13.5" r="8" fill="none" stroke="url(#g-gold)" strokeWidth="3" />
      <path d="M19.5 19.5 L27 27" stroke="url(#g-gold)" strokeWidth="4" strokeLinecap="round" />
    </Svg>
  );
}

export const COLOR_VAR: Record<string, string> = {
  brown: 'var(--c-brown)',
  grey: 'var(--c-grey)',
  blue: 'var(--c-blue)',
  yellow: 'var(--c-yellow)',
  red: 'var(--c-red)',
  green: 'var(--c-green)',
  purple: 'var(--c-purple)',
};

export function ColorChip({ color, size = 14, count }: { color: string; size?: number; count?: number }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: count !== undefined ? 'auto' : size * 0.72,
        minWidth: size * 0.72,
        height: size,
        padding: count !== undefined ? '0 4px' : 0,
        borderRadius: 3,
        background: COLOR_VAR[color],
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,.35), 0 1px 2px rgba(0,0,0,.5)',
        fontSize: size * 0.7,
        fontWeight: 700,
        color: color === 'yellow' ? '#2b1d0e' : '#fff',
        lineHeight: 1,
      }}
    >
      {count}
    </span>
  );
}
